<template>
  <div class="customer-view-page pa-6">
    <!-- Breadcrumb & Back Button -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-4">
      <div class="d-flex align-center text-caption breadcrumbs">
        <router-link to="/customer-service" class="breadcrumb-link text-muted">
          الرئيسية
        </router-link>
        <v-icon size="14" class="mx-1 text-muted">mdi-chevron-left</v-icon>
        <router-link to="/customer-service/customers" class="breadcrumb-link text-muted">
          العملاء
        </router-link>
        <v-icon size="14" class="mx-1 text-muted">mdi-chevron-left</v-icon>
        <span class="navy--text font-weight-bold">تفاصيل العميل</span>
      </div>

      <v-btn
        text
        color="#082C69"
        class="font-weight-bold text-none back-btn px-2"
        @click="$router.push('/customer-service/customers')"
      >
        <v-icon right size="18">mdi-arrow-right</v-icon>
        العودة إلى قائمة العملاء
      </v-btn>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="isLoading" class="py-12 text-center">
      <v-progress-circular indeterminate color="#082C69" size="48"></v-progress-circular>
      <p class="text-caption text-muted mt-3">جاري تحميل بيانات العميل...</p>
    </div>

    <!-- Content (When Loaded) -->
    <div v-else-if="customer">
      <!-- Customer Hero / Summary Card -->
      <v-card class="hero-card elevation-1 rounded-xl pa-6 mb-6">
        <div class="d-flex flex-wrap align-center justify-space-between pb-4 border-bottom mb-4">
          <!-- Avatar + Name + Reference + Status -->
          <div class="d-flex align-center">
            <v-avatar size="64" color="#EEF2F6" class="me-4 hero-avatar">
              <img v-if="customer.avatar_url" :src="customer.avatar_url" alt="avatar" />
              <span v-else class="navy--text font-weight-bold text-h6">
                {{ getInitials(customer.name) }}
              </span>
            </v-avatar>
            <div>
              <div class="d-flex align-center mb-1">
                <h2 class="text-h6 font-weight-bold navy--text me-2 mb-0">{{ customer.name }}</h2>
                <v-chip
                  small
                  class="font-weight-bold status-chip me-2"
                  :class="customer.status_id === 1 ? 'cs-status-chip-active' : 'cs-status-chip-suspended'"
                >
                  <span class="status-dot me-1" :class="customer.status_id === 1 ? 'dot-active' : 'dot-suspended'"></span>
                  <span class="cs-chip-text">{{ customer.status_label }}</span>
                </v-chip>
                <v-chip x-small class="font-weight-bold mono-code cs-ref-chip">
                  <span class="cs-chip-text">{{ customer.customer_ref }}</span>
                </v-chip>
              </div>
              <p class="text-caption text-muted mb-0">
                تاريخ الانضمام: {{ customer.registration_date }}
              </p>
            </div>
          </div>

          <!-- Change Status Action Button -->
          <div class="mt-3 mt-sm-0">
            <v-btn
              outlined
              :color="customer.status_id === 1 ? '#EF4444' : '#10B981'"
              class="font-weight-bold text-none rounded-lg"
              @click="openStatusModal"
            >
              <v-icon left size="16">{{ customer.status_id === 1 ? 'mdi-account-off-outline' : 'mdi-account-check-outline' }}</v-icon>
              {{ customer.status_id === 1 ? 'إيقاف الحساب' : 'تنشيط الحساب' }}
            </v-btn>
          </div>
        </div>

        <!-- Info Grid -->
        <v-row dense class="info-grid pt-2">
          <!-- Full Name -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">الاسم الكامل</span>
            <span class="text-body-2 font-weight-bold navy--text">{{ customer.name }}</span>
          </v-col>

          <!-- Phone Number -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">رقم الهاتف</span>
            <span dir="ltr" class="text-body-2 font-weight-bold text-dark">{{ customer.phone }}</span>
          </v-col>

          <!-- Email -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">البريد الإلكتروني</span>
            <span class="text-body-2 font-weight-bold text-dark">{{ customer.email }}</span>
          </v-col>

          <!-- Trip Count -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">إجمالي الرحلات</span>
            <span class="text-body-2 font-weight-bold text-blue">{{ customer.trip_count }} رحلات</span>
          </v-col>

          <!-- Last Trip -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">آخر رحلة</span>
            <span class="text-body-2 font-weight-bold text-dark">{{ customer.last_trip_date }}</span>
          </v-col>

          <!-- Customer Reference -->
          <v-col cols="12" sm="6" md="4" class="info-item pa-2">
            <span class="text-caption text-muted d-block">رقم العميل</span>
            <span class="text-body-2 font-weight-bold mono-code navy--text">{{ customer.customer_ref }}</span>
          </v-col>
        </v-row>
      </v-card>

      <!-- Section 1: Trip History (سجل الرحلات) -->
      <v-card class="section-card elevation-1 rounded-xl mb-6 overflow-hidden">
        <div class="d-flex align-center justify-space-between px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="#082C69" size="22" class="me-2">mdi-bus-clock</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold navy--text mb-0">سجل الرحلات</h3>
            <v-chip x-small class="font-weight-bold ms-2 cs-count-badge">
              <span class="cs-chip-text">{{ tripsHistory.length }}</span>
            </v-chip>
          </div>
        </div>

        <!-- Table -->
        <v-data-table
          v-if="tripsHistory.length > 0"
          :headers="tripHeaders"
          :items="displayedTrips"
          hide-default-footer
          class="custom-table"
        >
          <!-- # Index -->
          <template v-slot:item.index="{ item }">
            <span class="text-caption text-muted font-weight-medium">{{ item.index }}</span>
          </template>

          <!-- Booking Ref -->
          <template v-slot:item.booking_ref="{ item }">
            <span class="font-weight-bold mono-code navy--text text-caption">
              {{ item.booking_ref }}
            </span>
          </template>

          <!-- Trip Ref -->
          <template v-slot:item.trip_ref="{ item }">
            <span class="font-weight-bold mono-code text-caption text-dark">
              {{ item.trip_ref }}
            </span>
          </template>

          <!-- Route Name -->
          <template v-slot:item.route_name="{ item }">
            <span class="text-body-2 font-weight-medium text-dark">
              {{ item.route_name }}
            </span>
          </template>

          <!-- Date -->
          <template v-slot:item.date="{ item }">
            <span class="text-caption text-muted">{{ item.date }}</span>
          </template>

          <!-- Status -->
          <template v-slot:item.status_label="{ item }">
            <v-chip
              x-small
              class="font-weight-bold"
              :class="'cs-ride-status-' + item.ride_status"
            >
              <span class="cs-chip-text">{{ item.status_label }}</span>
            </v-chip>
          </template>

          <!-- Payment Method -->
          <template v-slot:item.payment_method="{ item }">
            <span class="text-caption font-weight-medium text-dark">
              {{ item.payment_method }}
            </span>
          </template>

          <!-- Amount -->
          <template v-slot:item.amount="{ item }">
            <span class="text-caption font-weight-bold navy--text">
              {{ item.amount }} جنيه
            </span>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              small
              color="#082C69"
              title="عرض تفاصيل الرحلة"
              @click="openTripDetails(item)"
            >
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Empty State for Trips -->
        <div v-else class="pa-8 text-center">
          <v-icon size="40" color="#CBD5E1" class="mb-2">mdi-bus-stop</v-icon>
          <p class="text-body-2 text-muted mb-0">لا يوجد رحلات مسجلة لهذا العميل حتى الآن</p>
        </div>

        <!-- View All / Collapse Button for Trips -->
        <div v-if="tripsHistory.length > 5" class="pa-3 text-center border-top">
          <v-btn
            text
            small
            color="#082C69"
            class="font-weight-bold text-none"
            @click="showAllTrips = !showAllTrips"
          >
            {{ showAllTrips ? 'عرض أقل' : 'عرض الكل (' + tripsHistory.length + ')' }}
            <v-icon right size="16">{{ showAllTrips ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
        </div>
      </v-card>

      <!-- Section 2: Contact History (سجل التواصل) -->
      <v-card class="section-card elevation-1 rounded-xl mb-6 overflow-hidden">
        <div class="d-flex align-center justify-space-between px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="#082C69" size="22" class="me-2">mdi-message-text-outline</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold navy--text mb-0">سجل التواصل</h3>
            <v-chip x-small class="font-weight-bold ms-2 cs-count-badge">
              <span class="cs-chip-text">{{ contactHistory.length }}</span>
            </v-chip>
          </div>
          <v-btn
            small
            depressed
            color="#082C69"
            class="white--text font-weight-bold rounded-lg"
            :to="`/customer-service/tickets?search=${customer.phone || customer.name || ''}`"
          >
            <v-icon small class="me-1">mdi-ticket-account</v-icon>
            عرض تذاكر العميل
          </v-btn>
        </div>

        <!-- Table if records exist -->
        <v-data-table
          v-if="contactHistory.length > 0"
          :headers="contactHeaders"
          :items="contactHistory"
          hide-default-footer
          class="custom-table"
        >
          <template v-slot:item.index="{ item }">
            <span class="text-caption text-muted font-weight-medium">{{ item.index }}</span>
          </template>
          <template v-slot:item.date="{ item }">
            <span class="text-caption text-muted">{{ item.date }}</span>
          </template>
          <template v-slot:item.channel="{ item }">
            <v-chip x-small class="cs-channel-chip">
              <span class="cs-chip-text">{{ item.channel }}</span>
            </v-chip>
          </template>
          <template v-slot:item.request_type="{ item }">
            <span class="text-caption font-weight-medium">{{ item.request_type }}</span>
          </template>
          <template v-slot:item.subject="{ item }">
            <span class="text-caption font-weight-bold text-dark">{{ item.subject }}</span>
          </template>
          <template v-slot:item.status_label="{ item }">
            <v-chip
              x-small
              class="font-weight-bold"
              :class="item.status_color === 'success' ? 'cs-status-chip-active' : 'cs-contact-status-open'"
            >
              <span class="cs-chip-text">{{ item.status_label }}</span>
            </v-chip>
          </template>
          <template v-slot:item.agent="{ item }">
            <span class="text-caption text-muted">{{ item.agent }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="#082C69" title="عرض التفاصيل" @click="selectedContact = item; contactDialog = true;">
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Clean Empty State for Contact History -->
        <div v-else class="pa-8 text-center">
          <v-icon size="40" color="#CBD5E1" class="mb-2">mdi-message-outline</v-icon>
          <p class="text-body-2 text-muted mb-0">لا يوجد سجل تواصل لهذا العميل حتى الآن</p>
        </div>
      </v-card>
    </div>

    <!-- Trip Read-Only Details Dialog -->
    <v-dialog v-model="tripDialog.open" max-width="500">
      <v-card v-if="tripDialog.item" class="rounded-xl pa-5">
        <v-card-title class="d-flex align-center justify-space-between pb-2">
          <div class="d-flex align-center text-subtitle-1 font-weight-bold navy--text">
            <v-icon left color="#082C69" size="22">mdi-ticket-confirmation-outline</v-icon>
            تفاصيل الحجز
          </div>
          <v-btn icon small @click="tripDialog.open = false">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-3">
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">رقم الحجز:</span>
            <span class="font-weight-bold mono-code navy--text">{{ tripDialog.item.booking_ref }}</span>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">رقم الرحلة:</span>
            <span class="font-weight-bold mono-code text-dark">{{ tripDialog.item.trip_ref }}</span>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">المسار:</span>
            <span class="font-weight-bold text-dark">{{ tripDialog.item.route_name }}</span>
          </div>
          <div v-if="tripDialog.item.start_stop" class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">نقطة الانطلاق:</span>
            <span class="font-weight-medium text-dark">{{ tripDialog.item.start_stop }}</span>
          </div>
          <div v-if="tripDialog.item.end_stop" class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">نقطة الوصول:</span>
            <span class="font-weight-medium text-dark">{{ tripDialog.item.end_stop }}</span>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">تاريخ الحجز:</span>
            <span class="font-weight-medium text-dark">{{ tripDialog.item.date }}</span>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">حالة الرحلة:</span>
            <v-chip
              x-small
              class="font-weight-bold"
              :class="'cs-ride-status-' + tripDialog.item.ride_status"
            >
              <span class="cs-chip-text">{{ tripDialog.item.status_label }}</span>
            </v-chip>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">طريقة الدفع:</span>
            <span class="font-weight-medium text-dark">{{ tripDialog.item.payment_method }}</span>
          </div>
          <div class="d-flex justify-space-between py-2 border-bottom">
            <span class="text-caption text-muted">عدد المقاعد:</span>
            <span class="font-weight-bold text-dark">{{ tripDialog.item.seats_count }} مقعد</span>
          </div>
          <div class="d-flex justify-space-between py-2">
            <span class="text-caption text-muted">إجمالي المبلغ المدفوع:</span>
            <span class="font-weight-bold navy--text text-subtitle-2">{{ tripDialog.item.amount }} جنيه</span>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-2">
          <v-spacer></v-spacer>
          <v-btn
            outlined
            color="#082C69"
            class="font-weight-bold text-none rounded-lg me-2"
            @click="$router.push('/customer-service/bookings/' + (tripDialog.item.booking_group_id || tripDialog.item.id))"
          >
            <v-icon left size="16">mdi-open-in-new</v-icon>
            فتح تفاصيل الحجز
          </v-btn>
          <v-btn color="#082C69" class="white--text text-none font-weight-bold px-4 rounded-lg" depressed @click="tripDialog.open = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Status Change Modal -->
    <v-dialog v-model="statusModal.open" max-width="480">
      <v-card class="rounded-xl pa-4">
        <v-card-title class="d-flex align-center text-h6 font-weight-bold navy--text pb-2">
          <v-icon left :color="statusModal.targetAction === 'suspend' ? '#EF4444' : '#10B981'" size="26">
            {{ statusModal.targetAction === 'suspend' ? 'mdi-account-alert-outline' : 'mdi-account-check-outline' }}
          </v-icon>
          تغيير حالة حساب العميل
        </v-card-title>

        <v-card-text class="pt-2 pb-4">
          <p class="text-body-1 font-weight-medium text-dark mb-2">
            هل تريد بالتأكيد {{ statusModal.targetAction === 'suspend' ? 'إيقاف' : 'تنشيط' }} حساب هذا العميل؟
          </p>
          <div class="info-alert pa-3 rounded-lg text-caption text-muted">
            <v-icon size="16" color="#64748B" class="me-1">mdi-information-outline</v-icon>
            لن يتم حذف الحساب أو بياناته. يمكن إعادة تفعيل الحساب في أي وقت.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-2">
          <v-spacer></v-spacer>
          <v-btn text class="text-none font-weight-bold" @click="statusModal.open = false">
            إلغاء
          </v-btn>
          <v-btn
            :color="statusModal.targetAction === 'suspend' ? '#EF4444' : '#10B981'"
            class="white--text text-none font-weight-bold px-4 rounded-lg"
            depressed
            :loading="statusModal.loading"
            @click="confirmStatusChange"
          >
            {{ statusModal.targetAction === 'suspend' ? 'تأكيد الإيقاف' : 'تأكيد التنشيط' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar.open" :color="snackbar.color" timeout="3500" top>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.open = false">إغلاق</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CustomerServiceCustomerDetails',
  data() {
    return {
      isLoading: true,
      customer: null,
      tripsHistory: [],
      contactHistory: [],
      showAllTrips: false,
      tripHeaders: [
        { text: '#', value: 'index', sortable: false, width: '40px' },
        { text: 'رقم الحجز', value: 'booking_ref', sortable: false, width: '130px' },
        { text: 'رقم الرحلة', value: 'trip_ref', sortable: false, width: '100px' },
        { text: 'المسار', value: 'route_name', sortable: false },
        { text: 'التاريخ', value: 'date', sortable: false, width: '110px' },
        { text: 'حالة الرحلة', value: 'status_label', sortable: false, width: '100px' },
        { text: 'طريقة الدفع', value: 'payment_method', sortable: false, width: '100px' },
        { text: 'المبلغ (جنيه)', value: 'amount', sortable: false, width: '110px' },
        { text: 'الإجراءات', value: 'actions', sortable: false, width: '70px', align: 'center' },
      ],
      contactHeaders: [
        { text: '#', value: 'index', sortable: false, width: '40px' },
        { text: 'التاريخ', value: 'date', sortable: false, width: '130px' },
        { text: 'القناة', value: 'channel', sortable: false, width: '110px' },
        { text: 'نوع الطلب', value: 'request_type', sortable: false, width: '100px' },
        { text: 'الموضوع', value: 'subject', sortable: false },
        { text: 'الحالة', value: 'status_label', sortable: false, width: '100px' },
        { text: 'الموظف المسؤول', value: 'agent', sortable: false, width: '140px' },
        { text: 'الإجراءات', value: 'actions', sortable: false, width: '70px', align: 'center' },
      ],
      tripDialog: {
        open: false,
        item: null,
      },
      statusModal: {
        open: false,
        targetAction: 'suspend',
        loading: false,
      },
      snackbar: {
        open: false,
        text: '',
        color: 'success',
      },
    };
  },
  computed: {
    displayedTrips() {
      const trips = Array.isArray(this.tripsHistory) ? this.tripsHistory : [];
      if (this.showAllTrips) {
        return trips;
      }
      return trips.slice(0, 5);
    },
  },
  mounted() {
    this.fetchCustomerDetails();
  },
  methods: {
    async fetchCustomerDetails() {
      this.isLoading = true;
      try {
        const customerId = this.$route.params.id;
        const response = await axios.get(`/customer-service/customers/${customerId}`);
        const resData = response && response.data ? response.data : {};
        this.customer = resData.customer || null;
        this.tripsHistory = Array.isArray(resData.trips_history) ? resData.trips_history : [];
        this.contactHistory = Array.isArray(resData.contact_history) ? resData.contact_history : [];
      } catch (error) {
        console.error('Error fetching customer details:', error);
        this.snackbar = {
          open: true,
          text: 'فشل في تحميل تفاصيل العميل',
          color: 'error',
        };
        if (error.response && error.response.status === 404) {
          setTimeout(() => {
            this.$router.push('/customer-service/customers');
          }, 1500);
        }
      } finally {
        this.isLoading = false;
      }
    },
    openStatusModal() {
      if (!this.customer) return;
      this.statusModal.targetAction = this.customer.status_id === 1 ? 'suspend' : 'activate';
      this.statusModal.open = true;
    },
    async confirmStatusChange() {
      if (!this.customer) return;
      this.statusModal.loading = true;

      try {
        const customerId = this.customer.id;
        const response = await axios.patch(`/customer-service/customers/${customerId}/status`);
        this.snackbar = {
          open: true,
          text: (response && response.data && response.data.message) || 'تم تحديث حالة العميل بنجاح',
          color: 'success',
        };
        this.statusModal.open = false;
        await this.fetchCustomerDetails();
      } catch (error) {
        console.error('Error updating status:', error);
        this.snackbar = {
          open: true,
          text: (error.response && error.response.data && error.response.data.message) || 'فشل في تحديث حالة العميل',
          color: 'error',
        };
      } finally {
        this.statusModal.loading = false;
      }
    },
    openTripDetails(item) {
      this.tripDialog.item = item;
      this.tripDialog.open = true;
    },
    getInitials(name) {
      if (!name) return 'ع';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    getTripStatusBg(status) {
      const map = {
        0: '#EFF6FF', // Upcoming
        1: '#ECFEFF', // In transit
        2: '#FFFBEB', // Missed
        3: '#ECFDF5', // Completed
        4: '#FEF2F2', // Cancelled
      };
      return map[status] || '#F1F5F9';
    },
    getTripStatusText(status) {
      const map = {
        0: '#1D4ED8',
        1: '#0E7490',
        2: '#D97706',
        3: '#059669',
        4: '#DC2626',
      };
      return map[status] || '#475569';
    },
  },
};
</script>

<style scoped>
.customer-view-page {
  font-family: 'Cairo', sans-serif !important;
  color: #1E293B;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #475569 !important;
  opacity: 1 !important;
}

.text-dark {
  color: #0F172A !important;
  opacity: 1 !important;
}

.text-blue {
  color: #1D4ED8 !important;
}

.breadcrumbs {
  font-size: 0.85rem;
}

.breadcrumb-link {
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #082C69 !important;
}

.back-btn:hover {
  background-color: rgba(8, 44, 105, 0.05);
}

.hero-card, .section-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.hero-avatar {
  border: 2px solid #E2E8F0;
}

.mono-code {
  font-family: 'Roboto Mono', monospace;
}

.status-chip {
  font-size: 0.75rem !important;
  height: 24px !important;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.dot-active {
  background-color: #10B981;
}

.dot-suspended {
  background-color: #EF4444;
}

.border-bottom {
  border-bottom: 1px solid #E2E8F0;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}

.info-grid .info-item {
  border-bottom: 1px solid #F1F5F9;
}

.custom-table ::v-deep th {
  background-color: #F8FAFC !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  border-bottom: 1px solid #E2E8F0 !important;
  height: 48px !important;
}

.custom-table ::v-deep td {
  border-bottom: 1px solid #F1F5F9 !important;
  height: 52px !important;
}

.info-alert {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

/* Customer Service Badges Text Clarity & Visibility */
.cs-status-chip-active {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-status-chip-active ::v-deep .v-chip__content,
.cs-status-chip-active .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-suspended {
  background-color: #FEF2F2 !important;
  border: 1px solid #FECACA !important;
}
.cs-status-chip-suspended ::v-deep .v-chip__content,
.cs-status-chip-suspended .cs-chip-text {
  color: #991B1B !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-ref-chip,
.cs-count-badge,
.cs-channel-chip {
  background-color: #EFF6FF !important;
  border: 1px solid #BFDBFE !important;
}
.cs-ref-chip ::v-deep .v-chip__content,
.cs-ref-chip .cs-chip-text,
.cs-count-badge ::v-deep .v-chip__content,
.cs-count-badge .cs-chip-text,
.cs-channel-chip ::v-deep .v-chip__content,
.cs-channel-chip .cs-chip-text {
  color: #1D4ED8 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-ride-status-1 {
  background-color: #ECFEFF !important;
  border: 1px solid #A5F3FC !important;
}
.cs-ride-status-1 ::v-deep .v-chip__content,
.cs-ride-status-1 .cs-chip-text {
  color: #0E7490 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-ride-status-2 {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-ride-status-2 ::v-deep .v-chip__content,
.cs-ride-status-2 .cs-chip-text {
  color: #B45309 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-ride-status-3 {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-ride-status-3 ::v-deep .v-chip__content,
.cs-ride-status-3 .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-ride-status-4 {
  background-color: #FEF2F2 !important;
  border: 1px solid #FECACA !important;
}
.cs-ride-status-4 ::v-deep .v-chip__content,
.cs-ride-status-4 .cs-chip-text {
  color: #991B1B !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-contact-status-open {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-contact-status-open ::v-deep .v-chip__content,
.cs-contact-status-open .cs-chip-text {
  color: #92400E !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}
</style>
